import type { Placement } from '@floating-ui/vue';

import { computed, ref, watch, type ComputedRef, type CSSProperties, type Ref } from 'vue';

import { useFloatingPosition } from '@/shared/lib/composables';

import type { DropdownAlign, DropdownSide } from '../types';

export interface UseDropdownOptions {
  offset?: number;
  align?: DropdownAlign;
  side?: DropdownSide;
  /** Жёсткий потолок высоты поповера в пикселях. */
  maxHeight?: number;
  /** Включить flip. По умолчанию true. */
  flip?: boolean;
  /** Включить shift. По умолчанию true. */
  shift?: boolean;
  /** Закрывать поповер, если триггер ушёл из видимой области. По умолчанию true. */
  closeOnReferenceHidden?: boolean;
}

export interface UseDropdownReturn {
  isOpen: Ref<boolean>;
  triggerRef: Ref<HTMLElement | null>;
  floatingRef: Ref<HTMLElement | null>;
  dropdownStyles: Ref<CSSProperties>;
  /** Угол поповера у триггера: оттуда он раскрывается. */
  transformOrigin: ComputedRef<string>;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

const toPlacement = (side?: DropdownSide, align?: DropdownAlign): Placement => {
  const base: 'top' | 'bottom' | 'left' | 'right' = side ?? 'bottom';
  if (align === 'start') return `${base}-start` as Placement;
  if (align === 'end') return `${base}-end` as Placement;
  if (align === 'center' && (base === 'top' || base === 'bottom')) {
    return base as Placement;
  }
  return base as Placement;
};

const OPPOSITE_SIDE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' } as const;

/** Точка, из которой раскрывается поповер с фактическим (после flip) размещением. */
export const toTransformOrigin = (placement: Placement): string => {
  const [side, align] = placement.split('-') as [keyof typeof OPPOSITE_SIDE, 'start' | 'end' | undefined];
  const isVertical = side === 'top' || side === 'bottom';
  const cross =
    align === undefined
      ? 'center'
      : (isVertical ? { start: 'left', end: 'right' } : { start: 'top', end: 'bottom' })[align];

  return isVertical ? `${cross} ${OPPOSITE_SIDE[side]}` : `${OPPOSITE_SIDE[side]} ${cross}`;
};

export const useDropdown = (options: UseDropdownOptions = {}): UseDropdownReturn => {
  const isOpen = ref(false);

  const {
    reference: triggerRef,
    floating: floatingRef,
    dropdownStyles,
    placement,
    isReferenceHidden,
  } = useFloatingPosition({
    open: isOpen,
    placement: toPlacement(options.side, options.align),
    offset: options.offset ?? 8,
    flip: options.flip,
    shift: options.shift,
    maxHeight: options.maxHeight,
  });

  const close = () => {
    isOpen.value = false;
  };

  const open = () => {
    isOpen.value = true;
  };

  const toggle = () => {
    isOpen.value = !isOpen.value;
  };

  if (options.closeOnReferenceHidden !== false) {
    watch(isReferenceHidden, (hidden) => {
      if (hidden && isOpen.value) close();
    });
  }

  return {
    isOpen,
    triggerRef: triggerRef as Ref<HTMLElement | null>,
    floatingRef,
    dropdownStyles,
    transformOrigin: computed(() => toTransformOrigin(placement.value)),
    toggle,
    open,
    close,
  };
};
